import { createContext } from 'react';
import type { Locale } from './types';

export interface I18nContextValue {
    /** The active locale code (e.g. `"en"`). */
    locale: Locale;
    /** Switches the active locale and persists the choice (`localStorage`). */
    setLocale: (locale: Locale) => void;
    /**
     * Translates `key` using the active locale's dictionary. Falls back to
     * `fallback` (or `key` itself) when the key is missing, so the UI never
     * renders a blank string while a translation is being added.
     */
    t: (key: string, fallback?: string) => string;
}

// A non-throwing default lets any component call `useI18n()` safely even when
// no `<LocaleProvider>` is mounted (e.g. a component rendered in isolation in
// a unit test) — it just behaves as if i18n were off: `t()` returns the
// fallback/key verbatim and `setLocale()` is a no-op.
export const defaultContextValue: I18nContextValue = {
    locale: 'en',
    setLocale: () => {},
    t: (key, fallback) => fallback ?? key,
};

// Kept in its own module (rather than alongside `LocaleProvider`/`useI18n`)
// so every consuming file only exports one thing — components or hooks, never
// both — which keeps React Fast Refresh working everywhere (oxlint's
// `react/only-export-components` rule).
export const I18nContext = createContext<I18nContextValue>(defaultContextValue);
